import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, Search, DoorOpen, Wrench, History, ChevronRight, Home, Pencil, Trash2, Copy } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, Loading, EmptyState, Money } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { formatTanggal, formatTanggalWaktu } from "@/lib/format";
import { toast } from "sonner";

function RoomCard({ room, onOpen }) {
  const accent = { tersedia: "#047857", dipesan: "#c2410c", menunggu_masuk: "#0369a1", terisi: "#334155" }[room.rental_status] || "#cbd5e1";
  return (
    <button
      onClick={onOpen}
      data-testid={`room-card-${room.number}`}
      className="group overflow-hidden rounded-xl border border-border bg-card text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="h-1.5" style={{ backgroundColor: accent }} />
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="text-lg font-bold">Kamar {room.number}</div>
          <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <StatusBadge kind="rental" value={room.rental_status} />
          <StatusBadge kind="condition" value={room.physical_condition} />
        </div>
        <div className="mt-3 space-y-0.5 text-xs text-muted-foreground">
          {room.current_resident_name ? (
            <div className="font-medium text-foreground">{room.current_resident_name}</div>
          ) : (
            <div>Belum ada penghuni/pemesan</div>
          )}
          {room.booking_deadline && <div>Batas booking: {formatTanggalWaktu(room.booking_deadline)}</div>}
          {room.lease_end_exclusive && <div>Akhir sewa: {formatTanggal(new Date(new Date(room.lease_end_exclusive).getTime() - 86400000))}</div>}
          <div className="pt-1 text-foreground"><Money value={room.pricing?.["3"]} /> <span className="text-muted-foreground">/ 3 bln</span></div>
        </div>
      </div>
    </button>
  );
}

export default function ProjekKamar() {
  const { user, isSuperadmin, canManage } = useAuth();
  const { projects, projectId, setProjectId } = useProjects();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [houseId, setHouseId] = useState(null);
  const [filters, setFilters] = useState({ q: "", rental: "all", cond: "all" });
  const [detail, setDetail] = useState(null);
  const [editProject, setEditProject] = useState(null);
  const [editHouse, setEditHouse] = useState(null);

  const hasProject = projectId && projectId !== "all";

  const deleteProject = async (p) => {
    if (!window.confirm(`Hapus proyek "${p.name}"? Hanya dapat dihapus jika proyek kosong (tanpa rumah & penghuni).`)) return;
    try { await api.delete(`/projects/${p.id}`); toast.success("Proyek dihapus."); qc.invalidateQueries({ queryKey: ["projects"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const { data: houses = [] } = useQuery({
    queryKey: ["houses", projectId],
    queryFn: async () => (await api.get("/houses", { params: { project_id: projectId } })).data,
    enabled: hasProject,
  });

  const deleteHouse = async (h) => {
    if (!window.confirm(`Hapus rumah "${h.name}"? Hanya dapat dihapus jika tidak ada kamar di dalamnya.`)) return;
    try { await api.delete(`/houses/${h.id}`); toast.success("Rumah dihapus."); setHouseId(null); qc.invalidateQueries({ queryKey: ["houses", projectId] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const activeHouse = houseId || houses[0]?.id || null;

  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ["rooms", activeHouse],
    queryFn: async () => (await api.get("/rooms", { params: { house_id: activeHouse } })).data,
    enabled: !!activeHouse,
  });

  const filtered = rooms.filter((r) =>
    (!filters.q || r.number.toLowerCase().includes(filters.q.toLowerCase())) &&
    (filters.rental === "all" || r.rental_status === filters.rental) &&
    (filters.cond === "all" || r.physical_condition === filters.cond)
  );

  if (!hasProject) {
    return (
      <div>
        <PageHeader title="Proyek & Kamar" description="Pilih proyek untuk melihat rumah kos dan kamar." actions={isSuperadmin && <CreateProject onDone={() => qc.invalidateQueries({ queryKey: ["projects"] })} />} />
        {projects.length === 0 ? (
          <EmptyState title="Belum ada proyek" description="Tambahkan proyek terlebih dahulu." icon={Building2} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <div key={p.id} className="group relative rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
                <button onClick={() => setProjectId(p.id)} data-testid={`project-card-${p.code}`} className="block w-full text-left">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><Building2 className="h-5 w-5" /></div>
                  <div className="mt-3 pr-14 text-base font-semibold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">Kode: {p.code}</div>
                </button>
                {isSuperadmin && (
                  <div className="absolute right-3 top-3 flex gap-1">
                    <button onClick={() => setEditProject(p)} data-testid={`project-edit-${p.code}`} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => deleteProject(p)} data-testid={`project-delete-${p.code}`} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {editProject && <EditProject project={editProject} onClose={() => setEditProject(null)} onDone={() => { setEditProject(null); qc.invalidateQueries({ queryKey: ["projects"] }); }} />}
      </div>
    );
  }

  const currentHouse = houses.find((h) => h.id === activeHouse);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Proyek & Kamar"
        description={projects.find((p) => p.id === projectId)?.name}
        actions={canManage && currentHouse && <CreateRoom houseId={activeHouse} onDone={() => qc.invalidateQueries({ queryKey: ["rooms", activeHouse] })} />}
      />

      {/* House selector */}
      <div className="flex flex-wrap items-center gap-2">
        {houses.map((h) => (
          <button key={h.id} onClick={() => setHouseId(h.id)} data-testid={`house-tab-${h.code}`}
            className={`rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors ${activeHouse === h.id ? "border-primary bg-primary text-white" : "border-border bg-card hover:bg-muted"}`}>
            {h.name} <span className={activeHouse === h.id ? "text-white/70" : "text-muted-foreground"}>· {h.room_count} kamar</span>
          </button>
        ))}
        {canManage && <CreateHouse projectId={projectId} onDone={() => qc.invalidateQueries({ queryKey: ["houses", projectId] })} />}
      </div>

      {currentHouse && (
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">Investor pemilik: <span className="font-medium text-foreground">{currentHouse.investor_name}</span></div>
          {canManage && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setEditHouse(currentHouse)} data-testid={`house-edit-${currentHouse.code}`}>
                <Pencil className="mr-1.5 h-4 w-4" /> Ubah Rumah
              </Button>
              <Button variant="ghost" size="sm" className="h-8 px-2 text-destructive hover:text-destructive" onClick={() => deleteHouse(currentHouse)} data-testid={`house-delete-${currentHouse.code}`}>
                <Trash2 className="mr-1.5 h-4 w-4" /> Hapus Rumah
              </Button>
            </div>
          )}
        </div>
      )}
      {editHouse && <EditHouse house={editHouse} onClose={() => setEditHouse(null)} onDone={() => { setEditHouse(null); qc.invalidateQueries({ queryKey: ["houses", projectId] }); }} />}

      {/* Filters */}
      <SectionCard testid="room-filters" className="!p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Cari nomor kamar..." className="h-9 pl-9" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="room-search" />
          </div>
          <Select value={filters.rental} onValueChange={(v) => setFilters((f) => ({ ...f, rental: v }))}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="room-filter-rental"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status Penyewaan</SelectItem>
              <SelectItem value="tersedia">Tersedia</SelectItem>
              <SelectItem value="dipesan">Dipesan (DP)</SelectItem>
              <SelectItem value="menunggu_masuk">Menunggu Masuk</SelectItem>
              <SelectItem value="terisi">Terisi</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.cond} onValueChange={(v) => setFilters((f) => ({ ...f, cond: v }))}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="room-filter-cond"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kondisi Fisik</SelectItem>
              <SelectItem value="siap">Siap Digunakan</SelectItem>
              <SelectItem value="disiapkan">Sedang Disiapkan</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </SectionCard>

      {roomsLoading ? <Loading /> : filtered.length === 0 ? (
        <EmptyState title="Tidak ada kamar" description="Sesuaikan filter atau tambah kamar." icon={DoorOpen} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((r) => <RoomCard key={r.id} room={r} onOpen={() => setDetail(r.id)} />)}
        </div>
      )}

      {detail && (
        <RoomDetailDialog roomId={detail} onClose={() => setDetail(null)}
          onBooking={(rid) => { setDetail(null); navigate("/booking-sewa", { state: { roomId: rid } }); }} />
      )}
    </div>
  );
}

function RoomDetailDialog({ roomId, onClose, onBooking }) {
  const { canManage } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["room", roomId], queryFn: async () => (await api.get(`/rooms/${roomId}`)).data });
  const [condOpen, setCondOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);

  const room = data?.room;
  const delRoom = async () => {
    if (!window.confirm(`Hapus Kamar ${room.number}? Hanya dapat dihapus jika tersedia & tanpa riwayat sewa.`)) return;
    try { await api.delete(`/rooms/${room.id}`); toast.success("Kamar dihapus."); onClose(); qc.invalidateQueries(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="room-detail-dialog">
        {isLoading || !room ? (
          <>
            <DialogTitle className="sr-only">Memuat detail kamar</DialogTitle>
            <Loading />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Kamar {room.number} <span className="text-sm font-normal text-muted-foreground">· {data.house?.name} · {data.project?.name}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <StatusBadge kind="rental" value={room.rental_status} />
                <StatusBadge kind="condition" value={room.physical_condition} />
              </div>
              {room.prep_note && (
                <div className="rounded-lg border px-3 py-2 text-xs" style={{ backgroundColor: "#fefce8", borderColor: "#fef08a", color: "#854d0e" }}>
                  <span className="font-semibold">Catatan persiapan: </span>{room.prep_note}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
                {["1", "3", "6", "12"].map((m) => (
                  <div key={m} className="rounded-lg border border-border p-3">
                    <div className="text-xs text-muted-foreground">Paket {m} bulan</div>
                    <div className="font-semibold"><Money value={room.pricing?.[m]} /></div>
                  </div>
                ))}
                <div className="rounded-lg border border-border p-3">
                  <div className="text-xs text-muted-foreground">IPL / bulan</div>
                  <div className="font-semibold"><Money value={room.ipl_monthly} /></div>
                </div>
              </div>

              {data.lease && (
                <div className="rounded-lg border border-border p-3 text-sm">
                  <div className="font-semibold">Penghuni saat ini</div>
                  <div className="text-muted-foreground">{data.resident?.name} · {data.lease.period_label}</div>
                  <div className="mt-1"><StatusBadge kind="lease" value={data.lease.status} /></div>
                </div>
              )}

              <div>
                <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><History className="h-4 w-4" /> Riwayat Sewa</div>
                {data.lease_history?.length ? (
                  <div className="space-y-1.5">
                    {data.lease_history.map((l) => (
                      <div key={l.id} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-xs">
                        <span>{l.resident_name} · {l.period_label}</span>
                        <StatusBadge kind="lease" value={l.status} />
                      </div>
                    ))}
                  </div>
                ) : <div className="text-xs text-muted-foreground">Belum ada riwayat.</div>}
              </div>
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={() => setCondOpen(true)} data-testid="room-condition-button"><Wrench className="mr-2 h-4 w-4" /> Ubah Kondisi</Button>
              <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="room-edit-button">
                <Pencil className="mr-2 h-4 w-4" /> {canManage ? "Ubah Kamar" : "Ubah Harga"}
              </Button>
              {canManage && <Button variant="outline" onClick={() => setDupOpen(true)} data-testid="room-duplicate-button"><Copy className="mr-2 h-4 w-4" /> Duplikat</Button>}
              {canManage && <Button variant="outline" className="text-destructive hover:text-destructive" onClick={delRoom} data-testid="room-delete"><Trash2 className="mr-2 h-4 w-4" /> Hapus</Button>}
              {room.rental_status === "tersedia" && (
                <Button onClick={() => onBooking(room.id)} data-testid="room-create-booking"><Home className="mr-2 h-4 w-4" /> Buat Booking</Button>
              )}
            </DialogFooter>

            {condOpen && <ConditionDialog room={room} onClose={() => setCondOpen(false)} onDone={() => { setCondOpen(false); qc.invalidateQueries(); }} />}
            {editOpen && <EditRoomDialog room={room} isSuperadmin={canManage} onClose={() => setEditOpen(false)} onDone={() => { setEditOpen(false); qc.invalidateQueries(); }} />}
            {dupOpen && <DuplicateRoomDialog room={room} onClose={() => setDupOpen(false)} onDone={() => { setDupOpen(false); qc.invalidateQueries(); }} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConditionDialog({ room, onClose, onDone }) {
  const [cond, setCond] = useState(room.physical_condition);
  const [note, setNote] = useState(room.prep_note || "");
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (cond === "disiapkan" && !note.trim()) { toast.error("Keterangan wajib diisi saat kamar sedang disiapkan."); return; }
    setLoading(true);
    try {
      await api.patch(`/rooms/${room.id}/condition`, { physical_condition: cond, prep_note: note });
      toast.success("Kondisi kamar diperbarui."); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Ubah Kondisi Fisik</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={cond} onValueChange={setCond}>
            <SelectTrigger data-testid="cond-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="siap">Siap Digunakan</SelectItem>
              <SelectItem value="disiapkan">Sedang Disiapkan</SelectItem>
            </SelectContent>
          </Select>
          {cond === "disiapkan" && (
            <div>
              <Label>Keterangan kondisi lapangan</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="cond-note" placeholder="Mis. cat dinding, perbaikan kamar mandi" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={loading} data-testid="cond-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditRoomDialog({ room, isSuperadmin, onClose, onDone }) {
  const [number, setNumber] = useState(room.number);
  const [p, setP] = useState({ 1: room.pricing["1"] || "", 3: room.pricing["3"], 6: room.pricing["6"], 12: room.pricing["12"], ipl: room.ipl_monthly });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!p[1] || !p[3] || !p[6] || !p[12]) { toast.error("Semua tarif paket (1, 3, 6, 12 bln) wajib diisi."); return; }
    const pricing = { 1: Number(p[1]), 3: Number(p[3]), 6: Number(p[6]), 12: Number(p[12]) };
    const ipl = Number(p.ipl);
    if (isSuperadmin && !String(number).trim()) { toast.error("Nomor kamar wajib diisi."); return; }
    setLoading(true);
    try {
      const numChanged = isSuperadmin && String(number).trim() !== String(room.number);
      const priceChanged = Number(room.pricing["3"]) !== pricing[3] || Number(room.pricing["6"]) !== pricing[6] ||
        Number(room.pricing["12"]) !== pricing[12] || Number(room.pricing["1"] || 0) !== Number(p[1] || 0) || Number(room.ipl_monthly) !== ipl;
      if (!numChanged && !priceChanged) { toast("Tidak ada perubahan."); setLoading(false); return; }
      if (numChanged) await api.patch(`/rooms/${room.id}`, { number: String(number).trim() });
      if (priceChanged) await api.patch(`/rooms/${room.id}/pricing`, { pricing, ipl_monthly: ipl });
      toast.success(isSuperadmin ? "Kamar diperbarui." : "Harga kamar diperbarui.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isSuperadmin ? `Ubah Kamar ${room.number}` : `Ubah Harga Kamar ${room.number}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {isSuperadmin && (
            <div><Label>Nomor Kamar</Label><Input value={number} onChange={(e) => setNumber(e.target.value)} data-testid="room-number-input" /></div>
          )}
          <div className="rounded-lg border border-dashed border-border p-3">
            <div className="mb-2 text-xs font-medium text-muted-foreground">Harga paket + IPL</div>
            <div className="mb-2"><Label className="text-xs">1 bln (tarif bulanan)</Label><Input type="number" value={p[1]} onChange={(e) => setP((s) => ({ ...s, 1: e.target.value }))} data-testid="price-1" /></div>
            <div className="grid grid-cols-3 gap-2">
              {["3", "6", "12"].map((m) => (
                <div key={m}><Label className="text-xs">{m} bln</Label><Input type="number" value={p[m]} onChange={(e) => setP((s) => ({ ...s, [m]: e.target.value }))} data-testid={`price-${m}`} /></div>
              ))}
            </div>
            <div className="mt-2"><Label className="text-xs">IPL / bulan</Label><Input type="number" value={p.ipl} onChange={(e) => setP((s) => ({ ...s, ipl: e.target.value }))} data-testid="price-ipl" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={loading} data-testid="room-edit-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DuplicateRoomDialog({ room, onClose, onDone }) {
  const [numbersRaw, setNumbersRaw] = useState("");
  const [targetHouse, setTargetHouse] = useState(room.house_id);
  const [p, setP] = useState({ 1: room.pricing["1"] || "", 3: room.pricing["3"], 6: room.pricing["6"], 12: room.pricing["12"], ipl: room.ipl_monthly });
  const [loading, setLoading] = useState(false);
  const { data: houses = [] } = useQuery({ queryKey: ["all-houses"], queryFn: async () => (await api.get("/houses")).data });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const parsed = numbersRaw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  const houseLabel = (h) => `${projects.find((pr) => pr.id === h.project_id)?.name || "?"} · ${h.name}`;
  const save = async () => {
    if (parsed.length === 0) { toast.error("Masukkan minimal satu nomor kamar baru."); return; }
    if (!targetHouse) { toast.error("Pilih rumah tujuan."); return; }
    if (!p[1] || !p[3] || !p[6] || !p[12]) { toast.error("Semua tarif paket (1, 3, 6, 12 bln) wajib diisi."); return; }
    setLoading(true);
    try {
      const res = await api.post(`/rooms/${room.id}/duplicate`, {
        numbers: parsed, house_id: targetHouse,
        pricing: { 1: Number(p[1]), 3: Number(p[3]), 6: Number(p[6]), 12: Number(p[12]) },
        ipl_monthly: Number(p.ipl),
      });
      const toName = houses.find((h) => h.id === targetHouse);
      toast.success(`${res.data.length} kamar dibuat${toName ? ` di ${toName.name}` : ""} dari tipe Kamar ${room.number}.`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="duplicate-room-dialog">
        <DialogHeader><DialogTitle>Duplikat Kamar {room.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Buat beberapa kamar sekaligus dengan tipe harga yang sama. Pilih rumah tujuan (boleh rumah/proyek lain), lalu ketik nomor kamar baru, pisahkan dengan koma atau baris baru.</p>
          <div>
            <Label>Rumah Tujuan</Label>
            <Select value={targetHouse} onValueChange={setTargetHouse}>
              <SelectTrigger data-testid="duplicate-target-house"><SelectValue placeholder="Pilih rumah" /></SelectTrigger>
              <SelectContent>{houses.map((h) => <SelectItem key={h.id} value={h.id}>{houseLabel(h)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nomor Kamar Baru</Label>
            <Textarea value={numbersRaw} onChange={(e) => setNumbersRaw(e.target.value)} placeholder="mis. A5, A6, A7" data-testid="duplicate-room-numbers" />
            {parsed.length > 0 && <p className="mt-1 text-xs text-muted-foreground">{parsed.length} kamar akan dibuat: {parsed.join(", ")}</p>}
          </div>
          <div className="rounded-lg border border-dashed border-border p-3">
            <div className="mb-2 text-xs font-medium text-muted-foreground">Harga paket + IPL (disalin dari Kamar {room.number}, dapat diubah)</div>
            <div className="mb-2"><Label className="text-xs">1 bln</Label><Input type="number" value={p[1]} onChange={(e) => setP((s) => ({ ...s, 1: e.target.value }))} data-testid="duplicate-price-1" /></div>
            <div className="grid grid-cols-3 gap-2">
              {["3", "6", "12"].map((m) => <div key={m}><Label className="text-xs">{m} bln</Label><Input type="number" value={p[m]} onChange={(e) => setP((s) => ({ ...s, [m]: e.target.value }))} data-testid={`duplicate-price-${m}`} /></div>)}
            </div>
            <div className="mt-2"><Label className="text-xs">IPL / bulan</Label><Input type="number" value={p.ipl} onChange={(e) => setP((s) => ({ ...s, ipl: e.target.value }))} data-testid="duplicate-price-ipl" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="duplicate-room-save">Buat Kamar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditProject({ project, onClose, onDone }) {
  const [f, setF] = useState({ name: project.name, code: project.code });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!f.name.trim() || !f.code.trim()) { toast.error("Nama & kode wajib diisi."); return; }
    setLoading(true);
    try { await api.patch(`/projects/${project.id}`, { name: f.name.trim(), code: f.code.trim() }); toast.success("Proyek diperbarui."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Ubah Proyek</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama Proyek</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="project-edit-name" /></div>
          <div><Label>Kode Proyek</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="project-edit-code" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="project-edit-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateProject({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", code: "" });
  const save = async () => {
    try { await api.post("/projects", f); toast.success("Proyek ditambahkan."); setOpen(false); setF({ name: "", code: "" }); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-project-button"><Plus className="mr-2 h-4 w-4" /> Tambah Proyek</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Tambah Proyek</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama Proyek</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="project-name" /></div>
          <div><Label>Kode Proyek</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="project-code" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="project-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditHouse({ house, onClose, onDone }) {
  const [f, setF] = useState({ name: house.name || "", address: house.address || "", investor_id: house.investor_id || "" });
  const [loading, setLoading] = useState(false);
  const { data: investors = [] } = useQuery({ queryKey: ["investors", house.project_id], queryFn: async () => (await api.get("/investors", { params: { project_id: house.project_id } })).data });
  const save = async () => {
    if (!f.name.trim()) { toast.error("Nama rumah wajib diisi."); return; }
    if (!f.investor_id) { toast.error("Pilih investor pemilik."); return; }
    setLoading(true);
    try { await api.patch(`/houses/${house.id}`, { name: f.name.trim(), address: f.address.trim(), investor_id: f.investor_id }); toast.success("Rumah diperbarui."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Ubah Rumah Kos</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama Rumah</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="house-edit-name" /></div>
          <div><Label>Alamat</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="house-edit-address" /></div>
          <div>
            <Label>Investor Pemilik</Label>
            <Select value={f.investor_id} onValueChange={(v) => setF({ ...f, investor_id: v })}>
              <SelectTrigger data-testid="house-edit-investor"><SelectValue placeholder="Pilih investor" /></SelectTrigger>
              <SelectContent>{investors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">Hanya investor proyek ini. Atur proyek investor di Pengaturan → Investor.</p>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="house-edit-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateHouse({ projectId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ code: "", name: "", address: "", investor_id: "" });
  const { data: investors = [] } = useQuery({ queryKey: ["investors", projectId], queryFn: async () => (await api.get("/investors", { params: { project_id: projectId } })).data, enabled: open && !!projectId });
  const save = async () => {
    try { await api.post("/houses", { ...f, project_id: projectId }); toast.success("Rumah ditambahkan."); setOpen(false); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="add-house-button"><Plus className="mr-1.5 h-4 w-4" /> Tambah Rumah</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Tambah Rumah Kos</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Kode Rumah</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="house-code" /></div>
          <div><Label>Nama Rumah</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="house-name" /></div>
          <div><Label>Alamat</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="house-address" /></div>
          <div>
            <Label>Investor Pemilik</Label>
            <Select value={f.investor_id} onValueChange={(v) => setF({ ...f, investor_id: v })}>
              <SelectTrigger data-testid="house-investor"><SelectValue placeholder="Pilih investor" /></SelectTrigger>
              <SelectContent>{investors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">Hanya investor proyek ini. Tambah investor baru di Pengaturan → Investor.</p>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="house-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateRoom({ houseId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ number: "", 1: "", 3: "", 6: "", 12: "", ipl: "" });
  const save = async () => {
    try {
      if (!f[1] || !f[3] || !f[6] || !f[12]) { toast.error("Semua tarif paket (1, 3, 6, 12 bln) wajib diisi."); return; }
      const pricing = { 1: Number(f[1]), 3: Number(f[3]), 6: Number(f[6]), 12: Number(f[12]) };
      await api.post("/rooms", { house_id: houseId, number: f.number, pricing, ipl_monthly: Number(f.ipl) });
      toast.success("Kamar ditambahkan."); setOpen(false); setF({ number: "", 1: "", 3: "", 6: "", 12: "", ipl: "" }); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-room-button"><Plus className="mr-2 h-4 w-4" /> Tambah Kamar</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Tambah Kamar</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nomor Kamar</Label><Input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="room-number" /></div>
          <div><Label>1 bln <span className="text-xs text-muted-foreground">(tarif sewa bulanan)</span></Label><Input type="number" value={f[1]} onChange={(e) => setF({ ...f, 1: e.target.value })} data-testid="room-price-1" /></div>
          <div className="grid grid-cols-3 gap-2">
            {["3", "6", "12"].map((m) => <div key={m}><Label>{m} bln</Label><Input type="number" value={f[m]} onChange={(e) => setF({ ...f, [m]: e.target.value })} data-testid={`room-price-${m}`} /></div>)}
          </div>
          <div><Label>IPL / bulan</Label><Input type="number" value={f.ipl} onChange={(e) => setF({ ...f, ipl: e.target.value })} data-testid="room-ipl" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="room-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
